import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { doc, setDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db, appId } from '../firebase';
import { errorMessage } from '../helpers';
import { initialWidgetVisibilityConst, getDefaultDashboardSettings, demoLiquidAssets, demoLiquidAssetsInterestRate, DEMO_CURRENCY_CONVERSION_RATES } from '../constants';
import { STALENESS_OPTIONS, DEFAULT_STALENESS_THRESHOLD } from '../logic/review';

export const useUserPreferences = (userId, isDemoMode, showModal, t, currentSnapshotId = null, activeSnapshot = null, updateSnapshot = null) => {
    const [liquidAssets, setLiquidAssets] = useState(0);
    const [liquidAssetsInterestRate, setLiquidAssetsInterestRate] = useState(0);
    // F28 — when the cash amount was last confirmed (anchor for its estimate).
    const [liquidAssetsAsOf, setLiquidAssetsAsOf] = useState(null);
    const [currency, setCurrency] = useState(() => localStorage.getItem('currency_v1') || 'CZK');
    
    const [dashboardSettings, setDashboardSettings] = useState(() => getDefaultDashboardSettings());
    
    const [portfolioFilter, setPortfolioFilter] = useState(() => localStorage.getItem('portfolioFilter_v1') || null);
    const [portfolioSortBy, setPortfolioSortBy] = useState(() => localStorage.getItem('portfolioSortBy_v1') || 'name_asc');
    const [portfolioGroupByType, setPortfolioGroupByType] = useState(() => localStorage.getItem('portfolioGroupByType_v1') !== 'false');
    const [portfolioShowInactive, setPortfolioShowInactive] = useState(() => localStorage.getItem('portfolioShowInactive_v1') !== 'false');
    
    const [scrollPositions, setScrollPositions] = useState({});

    // D3/D2 — the demo's own cash figure, held outside React state.
    //
    // The seeding effect below re-runs on a currency change and on every entry
    // to and exit from snapshot mode, and it used to re-apply the built-in demo
    // values each time. That silently discarded whatever the visitor had
    // changed — including, before D3, their whole dashboard layout. State alone
    // cannot fix it, because the snapshot branch legitimately overwrites
    // `liquidAssets` while a snapshot is being viewed and something has to
    // remember the live figure to restore afterwards. A real account has its
    // Firestore listener for that; the demo has this.
    //
    // `currency` rides along so a switch can re-express the amount the visitor
    // is looking at (old rate → new rate) instead of resetting it.
    const demoCashRef = useRef(null);

    useEffect(() => { localStorage.setItem('portfolioFilter_v1', portfolioFilter || ''); }, [portfolioFilter]);
    useEffect(() => { localStorage.setItem('portfolioSortBy_v1', portfolioSortBy); }, [portfolioSortBy]);
    useEffect(() => { localStorage.setItem('portfolioGroupByType_v1', String(portfolioGroupByType)); }, [portfolioGroupByType]);
    useEffect(() => { localStorage.setItem('portfolioShowInactive_v1', String(portfolioShowInactive)); }, [portfolioShowInactive]);
    useEffect(() => { localStorage.setItem('currency_v1', currency); }, [currency]);

    useEffect(() => {
        // Leaving the demo retires its scratch state, so a later demo starts
        // from the built-in example again.
        if (!isDemoMode) demoCashRef.current = null;

        if (currentSnapshotId && activeSnapshot) {
            setLiquidAssets(activeSnapshot.liquidAssets || 0);
            setLiquidAssetsInterestRate(activeSnapshot.liquidAssetsInterestRate || 0);
            setLiquidAssetsAsOf(null);
            return;
        }

        const defaultSettings = getDefaultDashboardSettings();

        const demoSettings = {
            ...defaultSettings,
            isScenarioActive: true,
        };

        if (isDemoMode) {
            const rate = DEMO_CURRENCY_CONVERSION_RATES[currency] || 1;
            if (!demoCashRef.current) {
                // First entry into the demo: seed from the built-in example, at
                // whatever currency is on. `dashboardSettings` is seeded HERE
                // AND ONLY HERE — re-applying it on every run of this effect is
                // what wiped the visitor's dashboard layout, dismissed
                // explanations and FIRE view whenever they changed currency or
                // came back from a snapshot.
                demoCashRef.current = {
                    amount: demoLiquidAssets / rate,
                    interestRate: demoLiquidAssetsInterestRate,
                    // Demo cash reads as confirmed today, so the review page's
                    // "Updated today" drawer isn't empty in the demo.
                    asOf: new Date(),
                    currency,
                };
                setDashboardSettings(demoSettings);
            } else if (demoCashRef.current.currency !== currency) {
                // Re-express the figure the visitor is looking at rather than
                // rebuilding it from the seed, so an edited amount survives.
                const prevRate = DEMO_CURRENCY_CONVERSION_RATES[demoCashRef.current.currency] || 1;
                demoCashRef.current = {
                    ...demoCashRef.current,
                    amount: demoCashRef.current.amount * prevRate / rate,
                    currency,
                };
            }
            setLiquidAssets(demoCashRef.current.amount);
            setLiquidAssetsInterestRate(demoCashRef.current.interestRate);
            setLiquidAssetsAsOf(demoCashRef.current.asOf);
            return;
        }
        if (!userId) {
            setLiquidAssets(0);
            setLiquidAssetsInterestRate(0);
            setLiquidAssetsAsOf(null);
            setDashboardSettings(defaultSettings);
            return;
        }

        const userSettingsDocPath = `artifacts/${appId}/users/${userId}/settings/dashboardPreferences`;
        const unsubscribe = onSnapshot(doc(db, userSettingsDocPath), (docSnapshot) => {
            if (docSnapshot.exists()) {
                const settingsData = docSnapshot.data();
                setLiquidAssets(settingsData.liquidAmount || 0);
                setLiquidAssetsInterestRate(settingsData.liquidInterestRate || 0);
                setLiquidAssetsAsOf(settingsData.liquidAssetsAsOf || null);

                const mergedWidgetVisibility = { ...initialWidgetVisibilityConst, ...(settingsData.widgetVisibility || {}) };
                setDashboardSettings({
                    widgetVisibility: mergedWidgetVisibility,
                    projectionInflationRate: typeof settingsData.projectionInflationRate === 'number' ? settingsData.projectionInflationRate : 0.02,
                    isProjectionInflationAdjusted: typeof settingsData.isProjectionInflationAdjusted === 'boolean' ? settingsData.isProjectionInflationAdjusted : false,
                    isScenarioActive: typeof settingsData.isScenarioActive === 'boolean' ? settingsData.isScenarioActive : false,
                    isProjectionGross: typeof settingsData.isProjectionGross === 'boolean' ? settingsData.isProjectionGross : false,
                    fireWithdrawalRate: typeof settingsData.fireWithdrawalRate === 'number' ? settingsData.fireWithdrawalRate : 0.04,
                    fireExpenseOverrideEnabled: typeof settingsData.fireExpenseOverrideEnabled === 'boolean' ? settingsData.fireExpenseOverrideEnabled : false,
                    fireExpenseOverrideAmount: typeof settingsData.fireExpenseOverrideAmount === 'number' ? settingsData.fireExpenseOverrideAmount : 0,
                    fireHorizonYears: typeof settingsData.fireHorizonYears === 'number' ? settingsData.fireHorizonYears : 40,
                    fireDrawdownEnabled: typeof settingsData.fireDrawdownEnabled === 'boolean' ? settingsData.fireDrawdownEnabled : false,
                    fireViewMode: (settingsData.fireViewMode === 'income' || settingsData.fireViewMode === 'fire') ? settingsData.fireViewMode : 'fire',
                    fireCoastTargetYear: typeof settingsData.fireCoastTargetYear === 'number' ? settingsData.fireCoastTargetYear : (new Date().getFullYear() + 25),
                    onboardingStatus: ['pending', 'completed', 'skipped'].includes(settingsData.onboardingStatus) ? settingsData.onboardingStatus : 'completed',
                    onboardingStep: typeof settingsData.onboardingStep === 'number' ? settingsData.onboardingStep : 0,
                    explanationsEnabled: typeof settingsData.explanationsEnabled === 'boolean' ? settingsData.explanationsEnabled : true,
                    dismissedExplanations: Array.isArray(settingsData.dismissedExplanations) ? settingsData.dismissedExplanations : [],
                    valueStalenessThreshold: STALENESS_OPTIONS.includes(settingsData.valueStalenessThreshold) ? settingsData.valueStalenessThreshold : DEFAULT_STALENESS_THRESHOLD,
                    // F5's other half: how much room a metric card takes. Lives
                    // with widgetVisibility because both are answers to the same
                    // question — what the dashboard shows and how densely.
                    cardDensity: settingsData.cardDensity === 'compact' ? 'compact' : 'full',
                });
            } else {
                setLiquidAssets(0);
                setLiquidAssetsInterestRate(0);
                setLiquidAssetsAsOf(null);
                setDashboardSettings(defaultSettings);
            }
        }, (error) => {
            console.error("Error fetching user settings:", error);
        });

        return () => unsubscribe();
    }, [userId, isDemoMode, currency, currentSnapshotId, activeSnapshot]); // Přidána závislost na měně

    const handleUpdateDashboardSettings = useCallback(async (newSettingsPartial) => {
        const newSettings = { ...dashboardSettings, ...newSettingsPartial };
        setDashboardSettings(newSettings);

        if (!isDemoMode && userId) {
            const settingsRef = doc(db, `artifacts/${appId}/users/${userId}/settings/dashboardPreferences`);
            try {
                await setDoc(settingsRef, newSettings, { merge: true });
            } catch (error) {
                console.error("Error writing dashboard settings:", error);
                showModal(t('warnings.settingsSaveError', { message: errorMessage(t, error) }));
                setDashboardSettings(dashboardSettings);
            }
        }
    }, [userId, isDemoMode, dashboardSettings, showModal, t]);

    const handleUpdateLiquidAssets = useCallback(async (values) => {
        const parsedAmount = parseFloat(values.liquidAssetsAmount);
        const parsedRate = parseFloat(values.liquidAssetsInterestRate || 0) / 100;
        if (isNaN(parsedAmount) || isNaN(parsedRate) || parsedRate < 0) {
            showModal(t('warnings.invalidNumber'));
            return;
        }
        // D2 — a snapshot stays read-only in the demo, so the refusal survives
        // here even though nothing else in demo refuses any more.
        if (isDemoMode && currentSnapshotId) { showModal(t('warnings.demoSnapshotReadOnly')); return; }
        setLiquidAssets(parsedAmount);
        setLiquidAssetsInterestRate(parsedRate);
        if (isDemoMode) {
            // Local only, but recorded in the ref so a currency switch or a
            // look at a snapshot cannot undo it.
            demoCashRef.current = { amount: parsedAmount, interestRate: parsedRate, asOf: new Date(), currency };
            setLiquidAssetsAsOf(demoCashRef.current.asOf);
            return;
        }
        if (currentSnapshotId && typeof updateSnapshot === 'function') {
            updateSnapshot(currentSnapshotId, { liquidAssets: parsedAmount, liquidAssetsInterestRate: parsedRate });
            return;
        }
        if (!userId) return;
        // Saving the cash dialog counts as confirming the amount, so the
        // review clock restarts here (F28).
        const confirmedAt = Timestamp.now();
        setLiquidAssetsAsOf(confirmedAt);
        const settingsRef = doc(db, `artifacts/${appId}/users/${userId}/settings/dashboardPreferences`);
        try {
            await setDoc(settingsRef, { liquidAmount: parsedAmount, liquidInterestRate: parsedRate, liquidAssetsAsOf: confirmedAt }, { merge: true });
        } catch (error) {
            showModal(t('warnings.settingsSaveError', { message: errorMessage(t, error) }));
        }
    }, [userId, isDemoMode, showModal, t, currentSnapshotId, updateSnapshot, currency]);

    // F28 — settling the cash row on the review page. `newValue` is the amount
    // to write, or null for "still correct" (amount stands, clock restarts).
    const confirmLiquidValue = useCallback(async (row, newValue) => {
        if (!row || row.kind !== 'liquid') return;
        if (currentSnapshotId) return;
        if (isDemoMode) {
            // D2 — the review page settles rows in the demo too, locally.
            const confirmedNow = new Date();
            demoCashRef.current = {
                ...(demoCashRef.current || { interestRate: liquidAssetsInterestRate, currency }),
                amount: newValue != null ? newValue : (demoCashRef.current?.amount ?? liquidAssets),
                asOf: confirmedNow,
            };
            if (newValue != null) setLiquidAssets(newValue);
            setLiquidAssetsAsOf(confirmedNow);
            return;
        }
        if (!userId) return;

        const confirmedAt = Timestamp.now();
        const payload = { liquidAssetsAsOf: confirmedAt };
        if (newValue != null) payload.liquidAmount = newValue;

        if (newValue != null) setLiquidAssets(newValue);
        setLiquidAssetsAsOf(confirmedAt);

        try {
            await setDoc(doc(db, `artifacts/${appId}/users/${userId}/settings/dashboardPreferences`), payload, { merge: true });
        } catch (error) {
            showModal(t('warnings.settingsSaveError', { message: errorMessage(t, error) }));
        }
    }, [userId, isDemoMode, showModal, t, currentSnapshotId, currency, liquidAssets, liquidAssetsInterestRate]);

    const uiPreferences = useMemo(() => ({
        portfolioFilter, setPortfolioFilter,
        portfolioSortBy, setPortfolioSortBy,
        portfolioGroupByType, setPortfolioGroupByType,
        portfolioShowInactive, setPortfolioShowInactive,
        scrollPositions, setScrollPositions,
        currency, setCurrency
    }), [
        portfolioFilter, setPortfolioFilter,
        portfolioSortBy, setPortfolioSortBy,
        portfolioGroupByType, setPortfolioGroupByType,
        portfolioShowInactive, setPortfolioShowInactive,
        scrollPositions, setScrollPositions,
        currency, setCurrency
    ]);

    return { liquidAssets, liquidAssetsInterestRate, liquidAssetsAsOf, dashboardSettings, handleUpdateDashboardSettings, handleUpdateLiquidAssets, confirmLiquidValue, uiPreferences };
};